import type { JSX } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { hasRefereeResponse } from '../utils/refereeResponse';
import type { Verification } from '../types/verification.types';

interface RefereeResponseProps {
  verification: Verification;
}

interface RefereeResponseToggleProps {
  isExpanded: boolean;
  onToggle: () => void;
}

interface ResponseEntryProps {
  label: string;
  value: string;
}

function ResponseEntry({ label, value }: ResponseEntryProps): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5 break-words">
      <span className="text-sm text-muted-foreground">{label}</span>
      <p className="whitespace-pre-line">{value}</p>
    </div>
  );
}

/**
 * Trigger for the collapsed-by-default response. Lives next to the response so
 * the table (extra row) and the card (inline) can place them independently.
 */
export function RefereeResponseToggle({
  isExpanded,
  onToggle,
}: RefereeResponseToggleProps): JSX.Element {
  const Chevron = isExpanded ? ChevronDown : ChevronRight;

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="px-0"
      aria-expanded={isExpanded}
      onClick={onToggle}
    >
      <Chevron className="mr-1 h-4 w-4" />
      {isExpanded ? 'Hide response' : 'View response'}
    </Button>
  );
}

/** What the referee actually wrote: the free-text comment plus every non-blank answer. */
export function RefereeResponse({ verification }: RefereeResponseProps): JSX.Element | null {
  if (!hasRefereeResponse(verification)) {
    return null;
  }

  const answers = (verification.refereeAnswers ?? []).filter(
    (answer) => answer.answerText.trim().length > 0
  );

  return (
    <div className="flex flex-col gap-3 text-left">
      {verification.refereeComment?.trim() && (
        <ResponseEntry label="Comment" value={verification.refereeComment} />
      )}
      {answers.map((answer) => (
        <ResponseEntry
          key={answer.questionId}
          label={answer.questionText}
          value={answer.answerText}
        />
      ))}
    </div>
  );
}
